// Copy for the per-course landing pages.
//
// One entry per course, keyed by the id in courseData. Everything factual —
// price, ages, hours, schedule, syllabus PDF — comes from COURSES; this file
// holds only what a landing page needs and the course world does not: the
// headline, the demo's framing, the topic list and the questions parents
// actually ask. The syllabus arrays mirror tools/syllabi-html/*, so a change
// to a course's topics belongs in both places.
import MineDemo from './demo/MineDemo';
import PhysicsDemo from './demo/PhysicsDemo';
import SceneDemo from './demo/SceneDemo';
import TurtleDemo from './demo/TurtleDemo';

/** Asked on every page, worded per course where the answer differs. */
const joinAnswer =
  'משאירים שם וטלפון בטופס, ואנחנו חוזרים אליכם לשיחה קצרה: מתאמים שיעור ניסיון חינם, ' +
  'מתאימים קבוצה לפי גיל ורמה, ועונים על כל מה שנשאר פתוח.';

const LANDING_CONTENT = {
  gamedev: {
    path: '/gamedev',
    Demo: PhysicsDemo,
    headline: ['הוא משחק במשחקים כבר שנים.', 'כאן הוא בונה אחד.'],
    lead:
      'קורס פיתוח משחקים במנוע Godot — מהסצנה הראשונה ועד משחק תלת-ממדי מלא ' +
      'שאפשר לשחק בו ולשלוח לחברים.',
    badges: ['שיעור ניסיון חינם', 'ללא ניסיון קודם בתכנות', 'פרויקט סיום: משחק מלא'],
    demoTitle: 'נסו בעצמכם: ידנית, או במנוע',
    demoSub:
      'שש קופסאות תלויות באוויר. אפשר להפיל אותן אחת-אחת, ואפשר לכתוב כמה שורות קוד ' +
      'שיפילו את כולן.',
    syllabusTitle: 'מה לומדים',
    syllabusSub:
      'המסלול עובר דרך כל מה שמרכיב משחק — לא רק קוד: סצנות, פיזיקה, אנימציה, ממשק וסאונד, ' +
      'עד לפרויקט תלת-ממדי מלא.',
    syllabus: [
      'היכרות עם מנוע Godot וממשק המשתמש',
      'סצנות, נודים (Nodes) ומערכת היררכית',
      'יסודות ב-GDScript — שפת התכנות של Godot',
      'יצירת אובייקטים, התנגשות ופיזיקה',
      'אנימציות, חלקיקים ואפקטים ויזואליים',
      'עיצוב ממשק משתמש (UI/UX) למשחק',
      'שמירת נתונים, סאונד ומעברי סצנות',
      'פרויקט סיום — פיתוח משחק תלת-ממדי מלא',
    ],
    faq: [
      {
        q: 'צריך ניסיון קודם בתכנות?',
        a: 'לא. מתחילים מהיכרות עם הממשק של Godot ומגיעים ל-GDScript בהדרגה, אחרי שכבר יש משהו שרץ על המסך. הדרישה היחידה היא היכרות בסיסית עם מחשב.',
      },
      {
        q: 'למה דווקא Godot?',
        a: 'מנוע חינמי בקוד פתוח, קל מספיק כדי שתהיה תוצאה על המסך כבר בשיעור הראשון, ואמיתי מספיק כדי שמפתחים מסחריים עובדים בו. אין רישיונות ואין תשלומים נסתרים.',
      },
      {
        q: 'מה צריך כדי להשתתף?',
        a: 'מחשב שמריץ את Godot — המנוע חינמי להורדה. אנחנו עוזרים בהתקנה לפני השיעור הראשון.',
      },
      {
        q: 'מה יוצא בסוף?',
        a: 'משחק תלת-ממדי שהתלמיד פיתח מאפס: שלבים, פיזיקה, ממשק וסאונד. משהו שאפשר לפתוח, לשחק בו ולהראות.',
      },
      { q: 'איך מצטרפים?', a: joinAnswer },
    ],
  },

  minecraft: {
    path: '/minecraft',
    Demo: MineDemo,
    headline: ['הילד שלכם כבר בונה במיינקראפט.', 'כאן הוא לומד לתכנת את זה.'],
    lead:
      'קורס תכנות במיינקראפט — מבלוקים של MakeCode ועד קוד Python אמיתי. ' +
      'אותו עולם שהוא מכיר, רק שהפעם הוא זה שכותב את החוקים.',
    badges: ['שיעור ניסיון חינם', 'ללא ניסיון קודם בתכנות', 'מסלול כניסה לפייתון'],
    demoTitle: 'נסו בעצמכם: ידנית, או בקוד',
    demoSub:
      '12 בלוקים. אפשר לכרות אותם אחד-אחד, ואפשר לכתוב שתי שורות שיעשו את זה בשבילכם. ' +
      'ההבדל בין השניים הוא בדיוק מה שלומדים בקורס.',
    syllabusTitle: 'מה לומדים',
    syllabusSub:
      'המסלול מתקדם מבלוקים לקוד כתוב, כך שכל מושג נלמד קודם ויזואלית ואז מתורגם לשפה — ' +
      'מיומנויות שעוברות לכל שפת תכנות אחרת.',
    syllabus: [
      'היכרות עם Minecraft Education וסביבת הקוד',
      'תכנות בבלוקים ב-MakeCode — פקודות, אירועים והסוכן',
      'לולאות — בנייה אוטומטית וחזרה על פעולות',
      'תנאים ומשתנים — קוד שמגיב למה שקורה בעולם',
      'מבלוקים לקוד כתוב — שורות ה-Python הראשונות',
      'פונקציות וארגון קוד — פירוק בעיה לחלקים',
      'רשימות, קואורדינטות ואלגוריתמים',
      'פרויקט סיום — תוכנית Python שפותרת אתגר בעולם',
    ],
    faq: [
      {
        q: 'צריך ניסיון קודם בתכנות?',
        a: 'לא. מתחילים מתכנות בבלוקים ב-MakeCode, שבו גוררים פקודות במקום לכתוב אותן, ורק אחרי שהמושגים ברורים עוברים לכתיבת קוד Python.',
      },
      {
        q: 'מה צריך כדי להשתתף?',
        a: 'מחשב עם Minecraft Education Edition. אין צורך בציוד נוסף, ואנחנו עוזרים בהתקנה לפני השיעור הראשון.',
      },
      {
        q: 'זה לא סתם עוד זמן מסך?',
        a: 'בשיעור לא משחקים במיינקראפט — מתכנתים אותו. הילד כותב קוד שבונה, כורה ופותר אתגרים בעולם, ורואה מיד מה הקוד שלו עשה. זה אותו עולם מוכר, בצד השני של המסך.',
      },
      {
        q: 'ומה קורה אחרי הקורס?',
        a: 'הקורס בנוי כמסלול כניסה טבעי לקורס פייתון המלא: אותם מושגים, אותה שפה, בלי סביבת המשחק. מי שרוצה להמשיך ממשיך משם.',
      },
      { q: 'איך מצטרפים?', a: joinAnswer },
    ],
  },

  python: {
    path: '/python',
    Demo: TurtleDemo,
    headline: ['השפה שמריצה היום כמעט הכול.', 'ואפשר להתחיל בה בגיל 10.'],
    lead:
      'קורס תכנות בפייתון — ממשתנה ראשון ועד אפליקציה שרצה. אותה שפה שבה כותבים ' +
      'בינה מלאכותית, אתרים וכלים אמיתיים.',
    badges: ['שיעור ניסיון חינם', 'ללא רקע קודם', 'פרויקט סיום: אפליקציה או משחק'],
    demoTitle: 'נסו בעצמכם: צעד-צעד, או בלולאה',
    demoSub:
      '36 צעדים מציירים כוכב. אפשר ללחוץ 36 פעם, ואפשר לכתוב שלוש שורות. ' +
      'זה השיעור הראשון בפייתון, והוא לא משתנה גם אחרי עשר שנים בתעשייה.',
    syllabusTitle: 'מה לומדים',
    syllabusSub:
      'מסלול יסודי ויישומי: כל נושא נלמד דרך משהו שרץ על המסך, עד לפרויקט סיום שהתלמיד בונה בעצמו.',
    syllabus: [
      'מבוא לשפת פייתון — משתנים, טיפוסים וקלט/פלט',
      'תנאים, לולאות ופונקציות',
      'רשימות, מילונים וקבצים',
      'עבודה עם מודולים וספריות חיצוניות',
      'תכנות מונחה עצמים (OOP)',
      'טיפול בשגיאות וכתיבת קוד יעיל',
      'פרויקטים יישומיים — משחקים קטנים, תוכנות וכלים',
      'פרויקט סיום — אפליקציה או משחק בפייתון',
    ],
    faq: [
      {
        q: 'צריך ניסיון קודם בתכנות?',
        a: 'לא. הקורס מתאים למתחילים גמורים, והדרישה היחידה היא סקרנות וחשיבה לוגית.',
      },
      {
        q: 'למה דווקא פייתון?',
        a: 'זו שפת הפתיחה הנפוצה בעולם: תחביר קריא שמאפשר להתמקד ברעיון ולא בפיסוק, ובמקביל שפה מקצועית לכל דבר — בינה מלאכותית, אתרים וניתוח נתונים רצים עליה.',
      },
      {
        q: 'זה לא יבש מדי לילדים?',
        a: 'לומדים דרך דברים שרצים: משחקים קטנים, כלים וציור. כל נושא נגמר במשהו שאפשר להריץ ולהראות, לא בתרגיל על דף.',
      },
      {
        q: 'מה צריך כדי להשתתף?',
        a: 'מחשב וחיבור לאינטרנט. סביבת הפיתוח חינמית, ואנחנו עוזרים בהתקנה לפני השיעור הראשון.',
      },
      { q: 'איך מצטרפים?', a: joinAnswer },
    ],
  },

  web3d: {
    path: '/web3d',
    Demo: SceneDemo,
    headline: ['האתר הזה נבנה בדיוק ככה.', 'וזה מה שלומדים בקורס.'],
    lead:
      'קורס פיתוח אתרים תלת-ממדיים ב-React ו-Three.js — מ-HTML ראשון ועד סצנה ' +
      'תלת-ממדית אינטראקטיבית שרצה בדפדפן.',
    badges: ['שיעור ניסיון חינם', 'ללא רקע קודם', 'פרויקט סיום: אתר תלת-ממדי'],
    demoTitle: 'נסו בעצמכם: ערכו את הקוד',
    demoSub:
      'הפקדים משנים את הסצנה ואת הקוד באותה רגע — כי בפיתוח אתרים תלת-ממדיים ' +
      'אלה אותו דבר. וזו בדיוק הספרייה שהאתר הזה בנוי בה.',
    syllabusTitle: 'מה לומדים',
    syllabusSub:
      'הקורס בנוי בשכבות: קודם דף שעובד, אחר כך React, ורק אז תלת-ממד — כל שלב עומד בפני עצמו.',
    syllabus: [
      'יסודות פיתוח אתרים — HTML, CSS ו-JavaScript',
      'מבוא ל-React — קומפוננטות, state ו-props',
      'עבודה עם JSX ו-Hooks',
      'יסודות גרפיקה תלת-ממדית ב-WebGL ו-Three.js',
      'יצירת סצנות, מצלמות, אורות וחומרים',
      'טעינת מודלים תלת-ממדיים ויצירת אינטראקטיביות',
      'שילוב Three.js בתוך React בעזרת react-three-fiber',
      'פרויקט סיום — אתר תלת-ממדי אינטראקטיבי מלא',
    ],
    faq: [
      {
        q: 'צריך ניסיון קודם בתכנות?',
        a: 'לא. מתחילים מ-HTML ו-CSS, כלומר מדף שאפשר לפתוח בדפדפן ולראות, ורק משם מתקדמים לתכנות.',
      },
      {
        q: 'זה לא מסובך מדי?',
        a: 'הקורס בנוי בשכבות, וכל שכבה שווה משהו בפני עצמה: מי שיעצור אחרי החלק הראשון כבר יודע לבנות אתר. התלת-ממד מגיע רק כשהבסיס יציב.',
      },
      {
        q: 'מה צריך כדי להשתתף?',
        a: 'מחשב ודפדפן. כל הכלים בקורס חינמיים.',
      },
      {
        q: 'למה זה שווה?',
        a: 'אלה הכלים של התעשייה עצמה: React היא הספרייה הנפוצה ביותר לפיתוח ממשקים, ו-Three.js היא הדרך שבה מריצים תלת-ממד בדפדפן. האתר שאתם קוראים בו עכשיו בנוי בשתיהן.',
      },
      { q: 'איך מצטרפים?', a: joinAnswer },
    ],
  },
};

export default LANDING_CONTENT;
